import { useEffect, useRef, useState } from 'react';
import {
  avatarImageUrl,
  getAvatarState,
  putAvatarSelection,
  uploadCustomAvatar,
} from '../api/avatar';
import { renameDisplayName } from '../api/players';
import { DefaultAvatarPicker } from './DefaultAvatarPicker';
import { usePlayerAvatar } from './usePlayerAvatar';
import { usePlayerCustomAvatar } from './usePlayerCustomAvatar';
import { usePlayerName } from './usePlayerName';

export interface AvatarPickerModalProps {
  /** The local player's id; null before the player has resolved (the upload +
   *  server-sync paths are disabled until it lands, but the local grid works). */
  playerId: string | null;
  onClose: () => void;
}

/** Downscale + centre-crop an image file to a {@code size}×{@code size} JPEG, so
 *  the upload is small (well under the server cap) and uniformly square. */
async function toSquareJpeg(file: File, size = 512, quality = 0.85): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unavailable');
  const scale = Math.max(size / bitmap.width, size / bitmap.height);
  const dw = bitmap.width * scale;
  const dh = bitmap.height * scale;
  ctx.drawImage(bitmap, (size - dw) / 2, (size - dh) / 2, dw, dh);
  bitmap.close?.();
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))),
      'image/jpeg',
      quality,
    );
  });
}

/**
 * Avatar picker — opened by tapping the avatar on the world map. Lets the
 * player (a) pick one of the twelve commissioned faces, persisted locally and
 * synced to the server, or (b) upload their own image, which is held for admin
 * review. A pending upload is shown to the uploader immediately (this device)
 * but isn't public until approved.
 */
export function AvatarPickerModal({ playerId, onClose }: AvatarPickerModalProps) {
  const [builtinId, setBuiltinId] = usePlayerAvatar();
  const [custom, setCustom] = usePlayerCustomAvatar();
  const [persistedName, setPersistedName] = usePlayerName();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectedReason, setRejectedReason] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Display-name editor.
  const [nameInput, setNameInput] = useState(persistedName ?? '');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameSaved, setNameSaved] = useState(false);

  async function saveName() {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === (persistedName ?? '')) return;
    if (!playerId) {
      setNameError('Still connecting — try again in a moment.');
      return;
    }
    setSavingName(true);
    setNameError(null);
    setNameSaved(false);
    try {
      const res = await renameDisplayName(playerId, trimmed);
      if (res.accepted) {
        setPersistedName(res.name ?? trimmed);
        setNameSaved(true);
      } else {
        setNameError(res.reason ?? "That name isn't allowed.");
      }
    } catch {
      setNameError('Couldn’t save your name — please try again.');
    } finally {
      setSavingName(false);
    }
  }

  // On open, refresh from the server so a just-reviewed result (approved /
  // rejected) is reflected even if this device missed the launch rehydrate.
  useEffect(() => {
    if (!playerId) return;
    let cancelled = false;
    getAvatarState(playerId)
      .then((s) => {
        if (cancelled) return;
        if (s.lastUploadStatus === 'REJECTED') setRejectedReason(s.rejectionReason ?? 'not accepted');
      })
      .catch(() => {/* non-fatal — the modal still works offline */});
    return () => {
      cancelled = true;
    };
  }, [playerId]);

  function pickBuiltin(id: string) {
    setBuiltinId(id);
    setCustom(null); // selecting a face drops any custom avatar (locally + server)
    setError(null);
    setRejectedReason(null);
    if (playerId) {
      putAvatarSelection(playerId, id).catch(() => {
        // Best-effort sync; the local choice still applies this session.
      });
    }
  }

  async function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file later
    if (!file) return;
    if (!playerId) {
      setError('Still connecting — try again in a moment.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const blob = await toSquareJpeg(file);
      const upload = await uploadCustomAvatar(playerId, blob);
      setRejectedReason(null);
      setCustom({
        uploadId: upload.id,
        url: avatarImageUrl(upload.id, playerId),
        status: 'PENDING',
      });
    } catch {
      setError('Upload failed. Please try a different image.');
    } finally {
      setUploading(false);
    }
  }

  const pending = custom?.status === 'PENDING';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Choose your avatar"
      onClick={onClose}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(8, 4, 22, 0.72)',
        backdropFilter: 'blur(2px)',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 360,
          maxHeight: '90vh',
          overflowY: 'auto',
          borderRadius: 18,
          background: 'linear-gradient(180deg, #221140 0%, #160826 100%)',
          border: '1px solid #34215c',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          padding: 20,
          color: '#ece7f5',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Your profile</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 'none', background: 'transparent', color: '#a394c4',
              fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: 4,
            }}
          >
            ×
          </button>
        </div>

        {/* Name */}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Your name</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              // Display-name field — NOT a credential. Password managers
              // (1Password / LastPass / Dashlane) otherwise heuristically
              // treat a lone text input as a username and inject their
              // autofill UI + fill a value straight into the DOM. On a
              // controlled input that fill never reaches React state, so
              // `nameInput` stayed '' while the box visibly showed a name —
              // which left the Save button stuck disabled (`!nameInput.trim()`)
              // and "did nothing" on click. These opt-outs suppress all three
              // managers so the field behaves as a plain nickname input.
              name="displayName"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-1p-ignore="true"
              data-lpignore="true"
              data-form-type="other"
              value={nameInput}
              maxLength={24}
              placeholder="Pick a name"
              onChange={(e) => {
                setNameInput(e.target.value);
                setNameError(null);
                setNameSaved(false);
              }}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid #34215c',
                background: '#160826',
                color: '#ece7f5',
                fontSize: 14,
              }}
            />
            <button
              type="button"
              // NB: do NOT disable on `!playerId`. saveName() guards that case
              // and shows "Still connecting — try again in a moment"; disabling
              // here instead would make the click a silent no-op (no onClick
              // fires on a disabled button), which is exactly the "Save does
              // nothing" symptom. Only block on in-flight / empty / unchanged.
              disabled={
                savingName || !nameInput.trim() ||
                nameInput.trim() === (persistedName ?? '')
              }
              onClick={saveName}
              style={{
                padding: '10px 16px',
                borderRadius: 10,
                border: 'none',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 14,
                color: '#fff',
                background: '#8b5cf6',
                opacity:
                  savingName || !nameInput.trim() ||
                  nameInput.trim() === (persistedName ?? '')
                    ? 0.6
                    : 1,
              }}
            >
              {savingName ? 'Saving…' : 'Save'}
            </button>
          </div>
          {nameError ? (
            <div style={{ fontSize: 12.5, color: '#ff9a9a', marginTop: 8 }}>{nameError}</div>
          ) : null}
          {nameSaved ? (
            <div style={{ fontSize: 12.5, color: '#7ee2a8', marginTop: 8 }}>Name updated.</div>
          ) : null}
          <p style={{ margin: '8px 0 0', fontSize: 11, color: '#8a7caf' }}>
            Names are checked before they go live.
          </p>
        </div>

        <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #34215c' }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Your avatar</div>
          <DefaultAvatarPicker value={pending || custom ? null : builtinId} onChange={pickBuiltin} />
        </div>

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #34215c' }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Upload your own</div>

          {pending ? (
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: 'rgba(245,185,69,0.12)', border: '1px solid rgba(245,185,69,0.4)',
                borderRadius: 12, padding: '10px 12px', marginBottom: 10,
              }}
            >
              {custom?.url ? (
                <img
                  src={custom.url}
                  alt=""
                  style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : null}
              <div style={{ fontSize: 12.5, color: '#f5d79a' }}>
                Your image is awaiting review. It'll go live once approved.
              </div>
            </div>
          ) : null}

          {rejectedReason && !pending ? (
            <div style={{ fontSize: 12.5, color: '#ff9a9a', marginBottom: 10 }}>
              Your last upload wasn't accepted ({rejectedReason}). You can try another.
            </div>
          ) : null}

          {error ? (
            <div style={{ fontSize: 12.5, color: '#ff9a9a', marginBottom: 10 }}>{error}</div>
          ) : null}

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onFileChosen}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            disabled={uploading || !playerId}
            onClick={() => fileRef.current?.click()}
            style={{
              width: '100%',
              padding: '11px 14px',
              borderRadius: 12,
              border: 'none',
              cursor: uploading || !playerId ? 'default' : 'pointer',
              fontWeight: 700,
              fontSize: 14,
              color: '#fff',
              background: uploading || !playerId ? '#3a2b5e' : '#8b5cf6',
              opacity: uploading || !playerId ? 0.7 : 1,
            }}
          >
            {uploading ? 'Uploading…' : pending ? 'Upload a different image' : 'Choose an image'}
          </button>
          <p style={{ margin: '8px 0 0', fontSize: 11, color: '#8a7caf' }}>
            Square images look best. Uploads are reviewed before they go public.
          </p>
        </div>
      </div>
    </div>
  );
}
